import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/authentication/auth.service';
import { UsersService } from '../user';
import { HttpContext } from '@angular/common/http';
import { OrganizationService } from '../organization';
import { getTimeZones } from '@vvo/tzdb';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root',
})

export class TimeZoneService {

  private readonly authService = inject(AuthService);
  private readonly usersService = inject(UsersService);
  private readonly organizationService = inject(OrganizationService);
  
  private browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private userTimeZone = signal<string | null>(null);
  private orgTimeZone = signal<string | null>(null);
  
  public readonly timeZones = getTimeZones().map(tz => ({
    name: tz.name,
    label: tz.rawFormat,
    offset: tz.currentTimeOffsetInMinutes
  }));
  
  public readonly displayTimeZone = computed(() => 
    this.userTimeZone() || this.orgTimeZone() || this.browserTimeZone
  );

  public readonly displayTimeOffset = computed(() => {
    const tz = this.timeZones.find(z => z.name === this.displayTimeZone());
    return tz?.offset ?? 0;
  });
  
  public isTimeZoneLoaded = signal<boolean>(false);

  constructor(){
    this.fetchUserTimeZone();

    effect(() => {
      if (this.userTimeZone() === null) {
        this.fetchOrgTimeZone();
      }
    });
  }

  public getUserTimeZone():string{
    return this.userTimeZone();
  }

  public setUserTimeZone(tz: string | null):void{
    this.userTimeZone.set(tz === 'Etc/UTC' ? null : tz);
  }

  public setOrgTimeZone(tz: string | null):void{
    this.orgTimeZone.set(tz === 'Etc/UTC' ? null : tz);
  }

  public async fetchUserTimeZone(){
    const observe:any = 'body';
    const options:any = { httpHeaderAccept: 'text/plain', context: HttpContext };
    try{
      const data = await firstValueFrom(
        this.usersService.apiUsersGetUserTimeZoneOrganizationIdUserIdGet(
          this.authService.organizationId,
          this.authService.user.profile.UserID,
          observe,
          false,
          options
        )
      );
      this.setUserTimeZone(data === 'Etc/UTC' ? null : data);
      if(data) this.isTimeZoneLoaded.set(true);
    }catch(error){
      this.setUserTimeZone(null);
    }
  }

  public async fetchOrgTimeZone(){
    const observe:any = 'body';
    const options:any = { httpHeaderAccept: 'text/plain', context: HttpContext };
    try{
      const data = await firstValueFrom(
        this.organizationService.apiOrganizationGetOrganizationTimeZoneOrganizationIdGet(
          this.authService.organizationId,
          observe,
          false,
          options
        )
      );
      this.setOrgTimeZone(data === 'Etc/UTC' ? null : data);
      this.isTimeZoneLoaded.set(true);
    } catch (error) {
      this.setOrgTimeZone(null);
    }
  }
  
  public getDate(date: string | number | Date): string {
    return this.formatByTimeZone(date, false);
  }

  public getTimestamp(date: string | number | Date): string {
    return this.formatByTimeZone(date, true);
  }

  public getTimetampWithMinDate(date: string | number | Date): string {
    if (!date) return 'N/A';

    let dateInput = date;
    if (typeof dateInput === 'string' && !dateInput.toLowerCase().endsWith('z')) {
      dateInput = dateInput + 'Z';
    }
    
    const parsedDate = new Date(dateInput);
    
    return parsedDate >= new Date('2000-01-01T00:00:00Z') 
      ? this.formatByTimeZone(dateInput, true) 
      : 'N/A';
  }
  
  private formatByTimeZone(date: string | number | Date, includeTime: boolean): string {
    if (!date) return 'N/A';
    
    let dateInput = date;
    if (typeof dateInput === 'string' && !dateInput.toLowerCase().endsWith('z')) {
      dateInput = dateInput + 'Z';
    }

    const parsedDate = new Date(dateInput);
    if (isNaN(parsedDate.getTime())) return 'N/A';

    const options: Intl.DateTimeFormatOptions = {
      timeZone: this.displayTimeZone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
      options.second = '2-digit';
      options.hour12 = false;
    }

    return new Intl.DateTimeFormat("sv-SE", options)
      .format(parsedDate)
      .replace(',', ''); 
  }
}
